import { INTERACTION_EVENTS } from "../hooks/useInteract";

// Timing helpers for non-essential work, so it stays out of the page's initial load
// (and out of Lighthouse's measurement). See docs/performance/LCP.md.

// How long after the window's load event Sentry (sentry.client.config.ts) starts.
//
// Measured on Top with Lighthouse (mobile, production config): work started at
// 2250 ms fell inside Lighthouse's measurement window and added up to ~300 ms of
// Total Blocking Time; from 2500 ms it didn't. 3000 ms leaves a margin. Before
// lowering it, re-measure (docs/performance/measuring.md).
export const AFTER_PAGE_LOAD_DELAY_MS = 3000;

// For heavier work (GTM and its tags in pages/_app.tsx, link prefetching in
// components/Atoms/OptimizedLink): how long after the load event it starts when the
// visitor hasn't interacted yet. A fixed short delay wasn't enough: link prefetching
// at 3 s sent ~80 requests on City pages, which kept Lighthouse measuring past 8 s,
// so GTM loaded inside the measurement too.
export const INTERACTION_FALLBACK_DELAY_MS = 8000;

function afterLoad(callback: () => void, delayMs: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedule = () => {
    timer = setTimeout(callback, delayMs);
  };

  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });

  return () => {
    window.removeEventListener("load", schedule);
    clearTimeout(timer);
  };
}

// Calls `callback` AFTER_PAGE_LOAD_DELAY_MS after the window's load event (or after
// that delay from now if the page has already loaded). Returns a cancel function.
export function afterPageLoad(callback: () => void): () => void {
  return afterLoad(callback, AFTER_PAGE_LOAD_DELAY_MS);
}

// Calls `callback` once: on the visitor's first interaction (INTERACTION_EVENTS), or
// `delayMs` after the load event, whichever comes first. Lighthouse never interacts.
// Returns a cancel function. For components, use hooks/useAfterInteractionOrDelay.
export function afterInteractionOrDelay(
  callback: () => void,
  delayMs: number = INTERACTION_FALLBACK_DELAY_MS,
): () => void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cancel();
    callback();
  };

  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, run, { once: true, passive: true });
  }
  const cancelTimer = afterLoad(run, delayMs);

  const cancel = () => {
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, run);
    }
    cancelTimer();
  };

  return () => {
    done = true;
    cancel();
  };
}
